import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Rating,
  Typography,
  useTheme,
} from "@mui/material";
import { useSelector } from "react-redux";

import { useGetCoachByIdQuery } from "../../../Store/api/queries";
import {
  useAddRatingMutation,
  useGetCoachRatingQuery,
  useGetMyCoachRatingQuery,
} from "../../../Store/api/ratingApi";

import SEOHelmet from "../../../Components/SeoHelmet";
import ShortDetails from "./ShortDetails";
import FullDetails from "./FullDetails";
import CDSkeleton from "./CDSkeleton";
import { showGlobalToast } from "../../../Components/ToastProvider";

export default function CoachDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const token = useSelector((state) => state.userSlice?.token);

  const [openRatingModal, setOpenRatingModal] = useState(false);
  const [userRatingValue, setUserRatingValue] = useState(0);

  const { data, isLoading } = useGetCoachByIdQuery(id);

  const {
    data: coachRatingData,
    isLoading: isCoachRatingLoading,
    isFetching: isCoachRatingFetching,
  } = useGetCoachRatingQuery(id, {
    skip: !id,
  });

  const {
    data: myRatingData,
    isLoading: isMyRatingLoading,
    isFetching: isMyRatingFetching,
  } = useGetMyCoachRatingQuery(id, {
    skip: !id || !token,
  });

  const [addRating, { isLoading: isRatingSubmitting }] =
    useAddRatingMutation();

  const coach = data?.data || {};

  const averageRating = Number(
    coachRatingData?.data?.averageRating ||
      coachRatingData?.data?.average_rating ||
      0
  );

  const totalRatings =
    Number(
      coachRatingData?.data?.totalRatings ||
        coachRatingData?.data?.total_ratings ||
        0
    ) || 0;

  const hasRated = Boolean(myRatingData?.data?.has_rated);
  const myRating = Number(myRatingData?.data?.rating || 0);

  useEffect(() => {
    if (hasRated && myRating >= 1 && myRating <= 5) {
      setUserRatingValue(myRating);
    }
  }, [hasRated, myRating]);

  const isRatingLoading = useMemo(() => {
    return (
      isCoachRatingLoading ||
      isCoachRatingFetching ||
      (token && (isMyRatingLoading || isMyRatingFetching))
    );
  }, [
    isCoachRatingLoading,
    isCoachRatingFetching,
    token,
    isMyRatingLoading,
    isMyRatingFetching,
  ]);

  const handleOpenRating = () => {
    if (!token) {
      showGlobalToast("لطفاً ابتدا وارد حساب کاربری خود شوید", "error");
      navigate("/auth");
      return;
    }

    if (hasRated) {
      setUserRatingValue(myRating);
    } else {
      setUserRatingValue(0);
    }

    setOpenRatingModal(true);
  };

  const handleCloseRating = () => {
    if (isRatingSubmitting) {
      return;
    }

    setOpenRatingModal(false);

    if (!hasRated) {
      setUserRatingValue(0);
    }
  };

  const handleSubmitRating = async () => {
    if (!token) {
      showGlobalToast("لطفاً ابتدا وارد حساب کاربری خود شوید", "error");
      navigate("/auth");
      return;
    }

    if (hasRated) {
      showGlobalToast("شما قبلاً به این مربی امتیاز داده‌اید", "error");
      return;
    }

    if (!userRatingValue || userRatingValue < 1 || userRatingValue > 5) {
      showGlobalToast("لطفاً یک امتیاز بین ۱ تا ۵ انتخاب کنید", "error");
      return;
    }

    try {
      const response = await addRating({
        targetId: id,
        data: {
          targetType: "Coach",
          rating: userRatingValue,
        },
      }).unwrap();

      if (response?.success) {
        showGlobalToast(
          response?.message || "امتیاز شما با موفقیت ثبت شد",
          "success"
        );
        setOpenRatingModal(false);
      }
    } catch (error) {
      const message =
        error?.data?.message || "ثبت امتیاز با مشکل مواجه شد";
      showGlobalToast(message, "error");
      console.error("Error submitting coach rating:", error);
    }
  };

  if (isLoading) {
    return <CDSkeleton />;
  }

  const pageTitle = coach?.fullname
    ? `مربی ${coach.fullname} | رزرو و مشاهده پروفایل`
    : "پروفایل مربی";

  const pageDesc =
    coach?.bio?.slice(0, 150) ||
    `پروفایل ورزشی مربی ${coach?.fullname} شامل تخصص‌ها، افتخارات و اطلاعات تماس.`;

  const coverImageUrl = coach?.images?.[0]
    ? import.meta.env.VITE_URL + coach.images[0]
    : undefined;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        bgcolor: "background.default",
        py: { xs: 2, md: 4 },
        direction: "rtl",
      }}
    >
      <SEOHelmet
        title={pageTitle}
        description={pageDesc}
        keywords={`مربی ورزشی, ${coach?.fullname || ""}, ${
          coach?.mainSpecialty?.name || ""
        }, فیتنس, بدنسازی`}
        ogTitle={pageTitle}
        ogDescription={pageDesc}
        ogImage={coverImageUrl}
      />

      <Container maxWidth="xxl" sx={{ px: { xs: 1.5, sm: 2, md: 4 } }}>
        <Box
          component="article"
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "270px 1fr",
              xl: "340px 1fr",
            },
            gap: { xs: 2, md: 3, lg: 4 },
            alignItems: "start",
          }}
        >
          <ShortDetails
            coach={coach}
            onOpenRating={handleOpenRating}
            averageRating={averageRating}
            totalRatings={totalRatings}
            myRating={myRating}
            hasRated={hasRated}
          />

          <FullDetails coach={coach} coachId={id} />
        </Box>
      </Container>

      <Dialog
        open={openRatingModal}
        onClose={handleCloseRating}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            direction: "rtl",
            borderRadius: 4,
            p: 1,
            bgcolor: "background.paper",
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.1rem",
            color: "primary.light",
          }}
        >
          {hasRated
            ? `امتیاز شما به ${coach?.fullname || "این مربی"}`
            : `ثبت امتیاز برای ${coach?.fullname || "این مربی"}`}
        </DialogTitle>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
            py: 3,
          }}
        >
          {isRatingLoading ? (
            <>
              <CircularProgress size={30} />
              <Typography variant="body2" color="text.secondary">
                در حال دریافت اطلاعات امتیاز...
              </Typography>
            </>
          ) : (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                {hasRated
                  ? "شما قبلاً امتیاز خود را ثبت کرده‌اید و امکان تغییر آن وجود ندارد."
                  : "عملکرد این مربی را چطور ارزیابی می‌کنید؟"}
              </Typography>

              <Box dir="ltr">
                <Rating
                  value={hasRated ? myRating : userRatingValue}
                  onChange={(_, newValue) => {
                    if (!hasRated) {
                      setUserRatingValue(newValue || 0);
                    }
                  }}
                  readOnly={hasRated}
                  size="large"
                  sx={{
                    fontSize: "2.5rem",
                    color: theme.palette.secondary.main,
                  }}
                />
              </Box>

              {hasRated ? (
                <Typography
                  variant="body2"
                  color="primary.main"
                  textAlign="center"
                  fontWeight={700}
                >
                  امتیاز ثبت‌شده شما:
                  {" "}
                  <Box
                    component="span"
                    dir="ltr"
                    sx={{ display: "inline-block" }}
                  >
                    {myRating} از ۵
                  </Box>
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  میانگین امتیاز این مربی:
                  {" "}
                  <Box
                    component="span"
                    dir="ltr"
                    sx={{ display: "inline-block", fontWeight: 700 }}
                  >
                    {averageRating.toFixed(1)}
                  </Box>
                  {" "}
                  از ۵
                  {" - "}
                  <Box
                    component="span"
                    dir="ltr"
                    sx={{ display: "inline-block", fontWeight: 700 }}
                  >
                    {totalRatings}
                  </Box>
                  {" "}
                  رأی
                </Typography>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "center",
            gap: 2,
            pb: 2,
          }}
        >
          <Button
            onClick={handleCloseRating}
            disabled={isRatingSubmitting}
            color="inherit"
            sx={{
              fontSize: "0.85rem",
              fontWeight: 700,
            }}
          >
            بستن
          </Button>

          {!hasRated && !isRatingLoading && (
            <Button
              variant="contained"
              onClick={handleSubmitRating}
              disabled={
                userRatingValue < 1 ||
                userRatingValue > 5 ||
                isRatingSubmitting
              }
              sx={{
                px: 4,
                borderRadius: 3,
                fontSize: "0.85rem",
                boxShadow: `0 4px 15px ${theme.palette.primary.main}60`,
              }}
            >
              {isRatingSubmitting ? "در حال ثبت..." : "ثبت امتیاز"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
